/**
 * Fund a Feature — donation/crowdfunding page that lets supporters
 * sponsor a specific feature to push it up the Bloq roadmap.
 *
 * Cognito form embed is admin-editable via /api/marketing/settings
 * (key: fund_a_feature_embed). The admin pastes the Cognito HTML into the
 * marketing-settings admin UI and it renders below. Legal terms live on
 * /fund-a-feature-terms, linked at the bottom of this page + in the footer.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, Rocket, Lightbulb, ListChecks } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import HtmlEmbed from "@/components/HtmlEmbed";
import api from "@/lib/api";

export default function FundAFeature() {
  const [embed, setEmbed] = useState("");
  useEffect(() => {
    api.get("/marketing/settings")
      .then((r) => setEmbed(r.data?.fund_a_feature_embed || ""))
      .catch(() => setEmbed(""));
  }, []);

  const steps = [
    {
      icon: Lightbulb,
      t: "Tell us what you want",
      d: "Describe the feature you'd love to see — a new integration, a workflow tweak, a brand-new module. The more specific, the better.",
    },
    {
      icon: HeartHandshake,
      t: "Pledge what it's worth to you",
      d: "Set the amount you'd contribute to bring it to life. Bigger pledges move features up the queue.",
    },
    {
      icon: ListChecks,
      t: "We scope and confirm",
      d: "Our team replies within 3 business days with a build estimate, timeline, and a yes/no on feasibility.",
    },
    {
      icon: Rocket,
      t: "We build it",
      d: "Funded features get scheduled into the public roadmap. You'll see progress updates as we ship.",
    },
  ];

  return (
    <div className="bg-[#fbfcfc] min-h-screen">
      <MarketingNav />

      <section className="bloq-section">
        <div className="bloq-container">
          <div className="max-w-3xl mb-16 bloq-fade-up">
            <p className="bloq-eyebrow">Fund a Feature</p>
            <h1 className="bloq-display text-5xl sm:text-6xl mt-3 mb-5">
              Push the feature you need <span className="bloq-gradient-text">to the front of the line.</span>
            </h1>
            <p className="text-[#4a4a52] text-lg leading-relaxed">
              You know what would make Bloq perfect for your workflow. We probably agree — and the
              build is already on a wishlist somewhere. Fund a Feature lets you sponsor the work
              directly, jump it to the front of our roadmap, and get the feature you actually need
              shipped on a real timeline.
            </p>
          </div>

          {/* 4-step explainer */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16" data-testid="fund-steps">
            {steps.map((s, i) => {
              const I = s.icon;
              return (
                <div key={i} className="bloq-card p-7 hover:-translate-y-1 transition-transform">
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
                       style={{ background: "rgba(0,94,122,0.1)", color: "#005e7a" }}>
                    <I className="w-5 h-5" />
                  </div>
                  <p className="text-[10px] uppercase tracking-widest text-[#82828c] mb-2">Step {i + 1}</p>
                  <p className="bloq-headline text-xl mb-2 text-[#005e7a]">{s.t}</p>
                  <p className="text-sm text-[#4a4a52] leading-relaxed">{s.d}</p>
                </div>
              );
            })}
          </div>

          {/* Submission form — Cognito embed slot */}
          <div className="bloq-glass-strong p-10 rounded-2xl" data-testid="fund-form-card">
            <p className="bloq-eyebrow">Submit your idea</p>
            <h2 className="bloq-headline text-3xl mt-3 max-w-xl mb-4">
              Tell us what you'd build, and what it's worth.
            </h2>
            <p className="text-[#4a4a52] mb-8 max-w-2xl">
              Fill in the form below with your idea, a rough pledge amount, and the easiest way to
              reach you. We'll review every submission and reply within 3 business days.
            </p>
            {embed ? (
              <HtmlEmbed
                html={embed}
                className="bloq-cognito-embed"
                testId="fund-cognito-embed"
              />
            ) : (
              <div className="border-2 border-dashed border-[#005e7a]/30 rounded-xl p-8 text-center bg-white/50">
                <p className="text-sm text-[#82828c] mb-4">Form coming soon — we're finalizing the submission flow.</p>
                <Link to="/contact?topic=fund-a-feature" className="bloq-btn-primary inline-flex items-center gap-2" data-testid="fund-fallback-cta">
                  Email us in the meantime <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>

          <p className="text-xs text-[#82828c] text-center mt-10">
            By submitting, you agree to the{" "}
            <Link to="/fund-a-feature-terms" className="text-[#005e7a] underline" data-testid="fund-terms-link">
              Fund a Feature terms
            </Link>.
            Pledges are not charged until your idea is scoped and confirmed.
          </p>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
